import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const fencingModule: ChecklistModuleDefinition = {
  code: 'fencing',
  name: 'Fencing & Boundaries',
  description: 'Perimeter fencing, cross-fencing, and gates',
  categories: [
    {
      code: 'perimeter_fencing',
      name: 'Perimeter Fencing',
      items: [
        {
          code: 'perimeter_fence_inventory',
          title: 'Perimeter fencing inventory',
          description: 'Document perimeter fence type and condition',
          isGroupTask: false,
          dataFields: [
            { key: 'total_linear_feet', label: 'Total Linear Feet', type: 'number', unit: 'feet' },
            { key: 'percent_fenced', label: 'Percent Perimeter Fenced', type: 'number', unit: 'percent' },
            { key: 'fence_types', label: 'Fence Types', type: 'multiselect', options: [
              { value: 'woven_wire', label: 'Woven Wire' },
              { value: 'barbed', label: 'Barbed Wire' },
              { value: 'high_tensile', label: 'High Tensile' },
              { value: 'board', label: 'Board Fence' },
              { value: 'pipe', label: 'Pipe/Metal' },
              { value: 'electric', label: 'Electric' },
              { value: 'chain_link', label: 'Chain Link' },
              { value: 'deer', label: 'Deer Fence (8ft+)' },
            ]},
            { key: 'average_age', label: 'Average Age', type: 'number', unit: 'years' },
          ],
        },
        {
          code: 'perimeter_condition',
          title: 'Perimeter fence condition',
          description: 'Assess fence condition by section',
          isGroupTask: false,
          dataFields: [
            { key: 'overall_condition', label: 'Overall Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'feet_needing_repair', label: 'Feet Needing Repair', type: 'number', unit: 'feet' },
            { key: 'feet_needing_replace', label: 'Feet Needing Replacement', type: 'number', unit: 'feet' },
            { key: 'post_condition', label: 'Post Condition', type: 'string' },
            { key: 'wire_condition', label: 'Wire Condition', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Good perimeter fence' },
            { flag: 'yellow', condition: 'Fence needs repairs' },
            { flag: 'red', condition: 'Major fence replacement needed' },
          ],
        },
        {
          code: 'livestock_containment',
          title: 'Livestock containment capability',
          description: 'Can fence contain intended livestock',
          isGroupTask: false,
          dataFields: [
            { key: 'intended_livestock', label: 'Intended Livestock', type: 'array' },
            { key: 'suitable_for_livestock', label: 'Suitable for Livestock', type: 'boolean' },
            { key: 'modifications_needed', label: 'Modifications Needed', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Fence suitable for livestock' },
            { flag: 'yellow', condition: 'Fence needs modification for livestock' },
            { flag: 'red', condition: 'Fence inadequate for livestock' },
          ],
        },
      ],
    },
    {
      code: 'cross_fencing',
      name: 'Cross Fencing',
      items: [
        {
          code: 'cross_fence_inventory',
          title: 'Cross-fencing inventory',
          description: 'Internal pasture divisions',
          isGroupTask: false,
          dataFields: [
            { key: 'number_of_pastures', label: 'Number of Pastures', type: 'number' },
            { key: 'cross_fence_feet', label: 'Cross Fence Feet', type: 'number', unit: 'feet' },
            { key: 'fence_types', label: 'Fence Types', type: 'array' },
          ],
        },
        {
          code: 'rotational_grazing',
          title: 'Rotational grazing setup',
          description: 'Assess pasture rotation capability',
          isGroupTask: false,
          dataFields: [
            { key: 'rotational_capable', label: 'Rotational Grazing Capable', type: 'boolean' },
            { key: 'paddock_count', label: 'Number of Paddocks', type: 'number' },
            { key: 'water_to_each', label: 'Water to Each Paddock', type: 'boolean' },
            { key: 'improvements_needed', label: 'Improvements Needed', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Well-designed rotational system' },
          ],
        },
      ],
    },
    {
      code: 'gates_access',
      name: 'Gates & Access',
      items: [
        {
          code: 'gate_inventory',
          title: 'Gate inventory',
          description: 'Document all gates',
          isGroupTask: false,
          dataFields: [
            { key: 'total_gates', label: 'Total Gates', type: 'number' },
            { key: 'gate_types', label: 'Gate Types', type: 'array' },
            { key: 'gate_widths', label: 'Gate Widths', type: 'string' },
          ],
        },
        {
          code: 'gate_condition',
          title: 'Gate condition',
          description: 'Assess gate condition',
          isGroupTask: false,
          dataFields: [
            { key: 'overall_condition', label: 'Overall Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'gates_needing_repair', label: 'Gates Needing Repair', type: 'number' },
            { key: 'security_adequate', label: 'Security Adequate', type: 'boolean' },
          ],
        },
        {
          code: 'cattle_guards',
          title: 'Cattle guards',
          description: 'Cattle guard assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'cattle_guards', label: 'Number of Cattle Guards', type: 'number' },
            { key: 'condition', label: 'Condition', type: 'string' },
            { key: 'replacement_needed', label: 'Replacement Needed', type: 'boolean' },
          ],
        },
      ],
    },
    {
      code: 'fencing_costs',
      name: 'Fencing Costs',
      items: [
        {
          code: 'fence_repair_estimate',
          title: 'Fence repair estimate',
          description: 'Cost to repair existing fencing',
          isGroupTask: true,
          dataFields: [
            { key: 'repair_linear_feet', label: 'Feet to Repair', type: 'number', unit: 'feet' },
            { key: 'repair_cost_per_foot', label: 'Cost Per Foot', type: 'number', unit: 'dollars' },
            { key: 'total_repair_cost', label: 'Total Repair Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'new_fence_estimate',
          title: 'New fencing estimate',
          description: 'Cost for new/additional fencing',
          isGroupTask: true,
          dataFields: [
            { key: 'new_fence_needed', label: 'New Fence Needed', type: 'number', unit: 'feet' },
            { key: 'fence_type_planned', label: 'Fence Type Planned', type: 'string' },
            { key: 'cost_per_foot', label: 'Cost Per Foot', type: 'number', unit: 'dollars' },
            { key: 'total_new_fence_cost', label: 'Total New Fence Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'total_fencing_budget',
          title: 'Total fencing budget',
          description: 'All fencing costs',
          isGroupTask: true,
          dataFields: [
            { key: 'repair_costs', label: 'Repair Costs', type: 'number', unit: 'dollars' },
            { key: 'new_fence_costs', label: 'New Fence Costs', type: 'number', unit: 'dollars' },
            { key: 'gate_costs', label: 'Gate Costs', type: 'number', unit: 'dollars' },
            { key: 'total_fencing', label: 'Total Fencing Budget', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Major fencing investment needed' },
          ],
        },
      ],
    },
  ],
};

